import * as React from "react";
import {
    PlasmicNoteViewer,
    DefaultNoteViewerProps
} from "./plasmic/main/PlasmicNoteViewer";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Note, Position} from "../models/fauna/dbSchema";
import EditorJs from "@natterstefan/react-editor-js";
import EditorJS from "@editorjs/editorjs";
import {EDITOR_JS_TOOLS} from "./EditorConstants";

export interface NoteViewerProps extends DefaultNoteViewerProps {
    displayedNote: any
    onChange?: (note: any, noteChanges: any) => any,
    onDelete: (note: any) => void,
    onClose: () => void,
    jumpTo: (position: Position, cameraPosition: Position) => void,
    showGoogleDriveSheet: any
}

function usePrevious(value: any): any {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

function NoteViewer({
                        displayedNote,
                        onChange,
                        onClose,
                        onDelete,
                        jumpTo,
                        showGoogleDriveSheet,
                        ...rest
                    }: NoteViewerProps) {
    const [editor, setEditor] = useState<EditorJS | null>(null);
    const prevState = usePrevious({displayedNote});
    const editorJSX = useMemo(() => {
            //This should only be called once with the same prevNote id and displayed note id
            return (<EditorJs
                data={displayedNote?.editorData}
                placeholder='Let`s make some notes here. Click on the plus on the left to format your notes.'
                readOnly={!onChange}
                tools={EDITOR_JS_TOOLS}
                editorInstance={editorInstance =>
                    setEditor(editor => {
                        if (prevState?.displayedNote && prevState?.displayedNote?.id !== displayedNote?.id) {
                            if (onChange && editor?.save) {
                                editor.save().then((editorData) => {
                                    if (editorData !== prevState.displayedNote.editorData) {
                                        onChange(prevState.displayedNote, {editorData});
                                    }
                                });
                            }
                            if (displayedNote?.editorData) {
                                editor?.render && editor.render(displayedNote.editorData);
                            } else {
                                editor?.clear && editor.clear();
                            }
                        }
                        return editorInstance;
                    })
                }
            />)
        }
        , [displayedNote?.editorData, displayedNote?.id, onChange, prevState?.displayedNote]);

    const onSave = useCallback(() => {
        if (onChange && editor?.save) {
            editor.save().then((editorData) => onChange(displayedNote, {editorData}));
        }
    }, [displayedNote, editor, onChange]);

    return (<React.Fragment>
        {displayedNote && <PlasmicNoteViewer
            {...rest}
            justView={!onChange}
            googleDriveIntegration={true}
            drivePlugin={{
                driveLinks: displayedNote?.googleDocs ?? [],
                onChange: (noteChanges: any) => {
                    onChange && onChange(displayedNote, noteChanges);
                },
                showGoogleDriveSheet: showGoogleDriveSheet
            }}
            notionIntegration={!!displayedNote.notionLink}
            notionButton={{
                onClick: () => {
                    window.open(displayedNote.notionLink, '_blank');
                }
            }}
            closeHandle={{
                onClick: () => {
                    onSave();
                    onClose();
                }
            }}
            noteLink={{
                defaultValue: 'bimmy.netlify.app/frame/' + displayedNote.id
            }}
            copyNoteLink={{
                onClick: () => navigator.clipboard.writeText('https://bimmy.netlify.app/frame/' + displayedNote.id)
            }}
            downloadButton={{
                onClick: () => {
                    const text =
                        '<?xml version="1.0" encoding="UTF-8"?> \n' +
                        '<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">\n' +
                        '<plist version="1.0">\n' +
                        '<dict>\n' +
                        '<key>URL</key>\n' +
                        '<string>https://bimmy.netlify.app/frame/' + displayedNote.id + '</string>' +
                        '</dict> \n' +
                        '</plist>'
                    const blob = new Blob([text], {type: 'webloc'});
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = getNoteTitle(displayedNote) +  ".webloc";
                    link.click();
                }
            }}
            deleteButton={{
                onClick: () => onDelete && onDelete(displayedNote)
            }}
            editor={{
                render: () => editorJSX
            }}
        />}
    </React.Fragment>)
}

export const getNoteTitle: (note: Note) => string = (note: any) => {
    if (note && note.editorData && note.editorData.blocks.length) {
        for (let i = 0; i < note.editorData.blocks.length; i++) {
            const block = note.editorData.blocks[i];
            if (block.type === 'header') {
                return block.data.text;
            }
        }
    }
    return 'Untitled';
}

export default NoteViewer;
